'use client';

import { useEffect, useState } from 'react';

import { useTheme } from 'next-themes';

import { useAuth } from '@clerk/nextjs';
import { useUpdateCurrentUser } from '@lib/auth/hooks/use-update-current-user';

import { APP_THEMES, type AppTheme, isThemeId } from './app-themes';

export interface UseAppThemeReturn {
  theme: string | undefined;
  resolvedTheme: string | undefined;
  setTheme: (theme: string) => void;
  /**
   * Applies a theme on this device only, without writing the
   * account preference. For server-driven syncs (account theme
   * → device, see ThemeAccountSync); user-initiated switches
   * go through `setTheme`.
   */
  setThemeLocal: (theme: string) => void;
  themes: string[];
  mounted: boolean;
  activeTheme: AppTheme | undefined;
}

export function useAppTheme(): UseAppThemeReturn {
  const [mounted, setMounted] = useState(false);
  const { theme, resolvedTheme, setTheme, themes } = useTheme();
  const { isSignedIn, userId } = useAuth();
  const updateCurrentUser = useUpdateCurrentUser();

  useEffect(() => {
    setMounted(true);
  }, []);

  const activeTheme = APP_THEMES.find(t => t.id === (resolvedTheme ?? theme));

  // next-themes' localStorage covers this device; `users.theme`
  // makes the choice follow the account across devices (read in
  // the root layout as ThemeProvider's defaultTheme). Signed-out
  // visitors keep the local-only behavior.
  const setThemePersisted = (newTheme: string) => {
    setTheme(newTheme);
    if (isSignedIn && userId && isThemeId(newTheme)) {
      updateCurrentUser.mutate({
        id: userId,
        configurations: { theme: newTheme },
      });
    }
  };

  return {
    theme,
    resolvedTheme,
    setTheme: setThemePersisted,
    setThemeLocal: setTheme,
    themes,
    mounted,
    activeTheme,
  };
}
